import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School'; // Or EmojiObjectsIcon for a lightbulb
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';

// Keyframes for animations
const stackBook = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseIcon = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

// Styled components
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '150px', // Ensure space for the loader
}));

const BookStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column-reverse', // So first book is at bottom
  alignItems: 'center',
});

const Book = styled(Box)(({ theme, delay }) => ({
  width: '80px',
  height: '12px',
  backgroundColor: theme.palette.primary.main,
  borderRadius: '2px',
  marginBottom: '5px', // Space between books
  opacity: 0, // Start hidden
  animation: `${stackBook} 0.5s ease-out forwards`,
  animationDelay: delay,
  boxShadow: `0px 2px 4px ${theme.palette.action.disabled}`, // Subtle shadow
  '&:nth-of-type(2)': { // Middle book slightly lighter or different shade
    backgroundColor: theme.palette.primary.light || // Use light if defined, else main
    (theme.palette.primary.main ? theme.palette.primary.main + 'B3' : '#67a7f3'), // 70% opacity of main
  },
  '&:first-of-type': { // Top book
    backgroundColor: theme.palette.primary.dark || // Use dark if defined, else main
    (theme.palette.primary.main ? theme.palette.primary.main + '80' : '#2a6bc7'), // 50% opacity of main
  }
}));


const IconContainer = styled(Box)(({ theme }) => ({
  marginTop: '15px',
  color: theme.palette.primary.main,
  opacity: 0, // Start hidden
  animation: `${fadeIn} 0.5s ease-out forwards, ${pulseIcon} 1.5s infinite ease-in-out`,
  animationDelay: '1.5s, 1.7s', // Delay fadeIn until books are stacked, then start pulsing
}));

const LoadingText = styled(Typography)(({ theme }) => ({
  marginTop: '20px',
  color: theme.palette.text.secondary,
  opacity: 0,
  animation: `${fadeIn} 0.8s ease-out forwards`,
  animationDelay: '0.5s', // Text fades in as books start stacking
}));


const Loader = () => {
  const language = localStorage.getItem("LANGUAGE") || "en"; // Default to English if not set
  const loadingText = language === "en" ? "Preparing Your Learning..." : "جارٍ تجهيز تعلمك..."; // Example translation

  return (
    <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} height={"100vh"}>
      <LoaderContainer>
        <BookStack>
          <Book delay="0.2s" /> {/* Bottom book */}
          <Book delay="0.7s" /> {/* Middle book */}
          <Book delay="1.2s" /> {/* Top book */}
        </BookStack>
        <IconContainer>
          {/* You can use EmojiObjectsIcon for a lightbulb idea */}
          {/* <EmojiObjectsIcon sx={{ fontSize: 30 }} /> */}
          <SchoolIcon sx={{ fontSize: 30 }} />
        </IconContainer>
        <LoadingText variant="body1">
          {loadingText}
        </LoadingText>
      </LoaderContainer>
    </Box>
  );
};

export default Loader;